import { FeatureCollection, Geometry, Position } from "geojson";
import {
  CustomGeoSource,
  CustomMouseEvent,
  defaultStrokeColor,
  mapActive,
  numbersAreNear,
} from "../utils/map-utils";
import { CustomSource } from "../models/CustomSource";
import { GeoJsonNodeProps } from "../../osaway/models/CustomGeoJsonProperties";
import { OsaNode } from "../../osaway/models/OsaNode";
import { OsaWay } from "../../osaway/models/OsaWay";
import { HlaWay } from "../../ra/models/HlaWay";
import { RootStore } from "../../shared/stores/RootStore";
import { LifeCycleStage } from "../../osaway/models/LifeCycleStage";
import turf from "../../shared/utils/turf";

export class OsaMapSource {
  map: mapboxgl.Map;
  rootStore: RootStore;
  locationChangeListenerRemovers: (() => void)[] = [];

  constructor(map: mapboxgl.Map, rootStore: RootStore) {
    this.map = map;
    this.rootStore = rootStore;
    this.config();
  }

  clear() {
    if (mapActive(this.map)) {
      try {
        this.disableLocationChange();

        this.map.removeLayer(simplifiedStrokeLayerConfig.id);
        this.map.removeLayer(simplifiedPointLayerConfig.id);
        this.map.removeLayer(simplifiedLineLayerConfig.id);
        this.map.removeLayer(simplifiedDirectionLayerConfig.id);
        this.map.removeSource(simplifiedSourceName);

        this.map.removeLayer(strokeLayerConfig.id);
        this.map.removeLayer(pointMultiSelectedLayerConfig.id);
        this.map.removeLayer(pointStrokeLayerConfig.id);
        this.map.removeLayer(pointInvisibleLayerConfig.id);
        this.map.removeLayer(lineMultiSelectedLayerConfig.id);
        this.map.removeLayer(lineLayerConfig.id);
        this.map.removeLayer(pointLayerConfig.id);
        this.map.removeLayer(directionLayerConfig.id);
        this.map.removeLayer(commentLayerConfig.id);
        this.map.removeSource(sourceName);
      } catch (e) {
        console.warn("Error while removing layers and sources", e);
      }
    }
  }

  disableLocationChange() {
    this.locationChangeListenerRemovers.map((fn) => fn());
    this.locationChangeListenerRemovers = [];
  }

  private config() {
    if (mapActive(this.map) && !this.map.getSource(sourceName)) {
      this.map.addSource(simplifiedSourceName, simplifiedSourceConfig);
      this.map.addLayer(simplifiedStrokeLayerConfig);
      this.map.addLayer(simplifiedPointLayerConfig);
      this.map.addLayer(simplifiedLineLayerConfig);
      this.map.addLayer(simplifiedDirectionLayerConfig);

      this.map.addSource(sourceName, sourceConfig);
      this.map.addLayer(strokeLayerConfig);
      this.map.addLayer(pointMultiSelectedLayerConfig);
      this.map.addLayer(pointStrokeLayerConfig);
      this.map.addLayer(pointInvisibleLayerConfig);
      this.map.addLayer(lineMultiSelectedLayerConfig);
      this.map.addLayer(lineLayerConfig);
      this.map.addLayer(pointLayerConfig);
      this.map.addLayer(directionLayerConfig);
      this.map.addLayer(commentLayerConfig);
    }
  }

  update(geoJson: FeatureCollection) {
    if (mapActive(this.map)) {
      const source = this.map.getSource(sourceName) as mapboxgl.GeoJSONSource;
      source?.setData(geoJson);

      const simplifiedSource = this.map.getSource(
        simplifiedSourceName,
      ) as mapboxgl.GeoJSONSource;
      const simplifiedGeoJson = buildSimplifiedGeoJson(geoJson);
      simplifiedSource?.setData(simplifiedGeoJson);
    }
  }

  enableLocationChanges() {
    const { osaNodeStore } = this.rootStore;
    const canvas = this.map.getCanvasContainer();

    const cursorMove = () => (canvas.style.cursor = "move");
    const cursorDefault = () => (canvas.style.cursor = "");

    this.map.on("mouseenter", pointStrokeLayerName, cursorMove);
    this.map.on("mouseleave", pointStrokeLayerName, cursorDefault);

    const mouseDrag = (e: CustomMouseEvent) => {
      e.preventDefault();
      const feature = e.features?.[0]!;
      canvas.style.cursor = "grab";

      // updates the map features without changing the app state
      const onMouseMove = (event: mapboxgl.MapMouseEvent) => {
        canvas.style.cursor = "grabbing";
        const coords = event.lngLat;
        const source = this.map.getSource(sourceName) as CustomGeoSource;

        // update the coords of the point that was dragged
        source._data.features.forEach((feat, index) => {
          if (feat.properties?.id === feature.id) {
            if (feat.geometry.type === "Point") {
              // needs to be a new instance, to prevent mutation of the original state
              feat.geometry = {
                type: "Point",
                coordinates: [coords.lng, coords.lat],
              };
            }
          }
        });

        source.setData(source._data);
      };

      this.map.on("mousemove", onMouseMove);

      this.map.once("mouseup", (event: mapboxgl.MapMouseEvent) => {
        canvas.style.cursor = "";
        const props = feature.properties as GeoJsonNodeProps;
        const { lat, lng } = event.lngLat;

        const node = osaNodeStore.get(props.id);

        if (node) {
          const locationChanged = !(
            numbersAreNear(node.latitude, lat) &&
            numbersAreNear(node.longitude, lng)
          );
          if (locationChanged) {
            osaNodeStore.setNodeCoords(node.id, lat, lng);
          }
        }

        this.map.off("mousemove", onMouseMove);
      });
    };

    this.map.on("mousedown", pointStrokeLayerName, mouseDrag);

    this.locationChangeListenerRemovers.push(() => {
      this.map.off("mouseenter", pointStrokeLayerName, cursorMove);
      this.map.off("mouseleave", pointStrokeLayerName, cursorDefault);
      this.map.off("mousedown", pointStrokeLayerName, mouseDrag);
    });
  }

  configCursorOnHover() {
    const canvas = this.map.getCanvasContainer();

    const cursorPointer = () => (canvas.style.cursor = "pointer");
    const cursorDefault = () => (canvas.style.cursor = "");

    this.map.on("mouseenter", pointStrokeLayerName, cursorPointer);
    this.map.on("mouseleave", pointStrokeLayerName, cursorDefault);
    this.map.on("mouseenter", simplifiedStrokeLayerName, cursorPointer);
    this.map.on("mouseleave", simplifiedStrokeLayerName, cursorDefault);
    this.map.on("mouseenter", strokeLayerName, cursorPointer);
    this.map.on("mouseleave", strokeLayerName, cursorDefault);
  }

  enableOsaWaySelection() {
    const { osaWayStore, osaNodeStore } = this.rootStore;
    const canvas = this.map.getCanvasContainer();

    const handleSelectRoute = (e: CustomMouseEvent) => {
      e.preventDefault();
      canvas.style.cursor = "grab";
      const feature = e.features?.[0]!;
      const props = feature.properties as GeoJsonNodeProps;

      this.map.once("mouseup", () => {
        canvas.style.cursor = "";

        if (osaNodeStore.getSelected()?.osaWayId !== props.osaWayId) {
          osaNodeStore.clearSelected();
        }
        osaWayStore.select(props.osaWayId);
      });
    };
    this.map.on("mousedown", simplifiedStrokeLayerName, handleSelectRoute);
  }

  enableNodeSelection() {
    const { osaWayStore, osaNodeStore, mapStore } = this.rootStore;
    const canvas = this.map.getCanvasContainer();

    const handleSelectNode = (e: CustomMouseEvent) => {
      e.preventDefault();
      canvas.style.cursor = "grab";
      const feature = e.features?.[0]!!;
      const props = feature.properties as GeoJsonNodeProps;

      this.map.once("mouseup", () => {
        canvas.style.cursor = "";

        const designingRoute = !!(
          osaWayStore.getSelected()?.lifeCycleStage === LifeCycleStage.Design &&
          mapStore.newPlannedTrajectory
        );
        if (!designingRoute) {
          osaWayStore.select(props.osaWayId);
          osaNodeStore.select(props.id);
        }
      });
    };

    this.map.on("mousedown", pointStrokeLayerName, handleSelectNode);
    this.map.on("mousedown", commentLayerName, handleSelectNode);
    this.map.on("mousedown", strokeLayerName, handleSelectNode);
  }

  extendBounds(
    bounds: mapboxgl.LngLatBounds,
    geoJson: FeatureCollection,
    options: ExtendBoundsOptions,
  ) {
    geoJson.features.forEach((feature) => {
      const props = feature.properties as GeoJsonNodeProps;

      if (shouldIncludeInBounds(props, options)) {
        const geometry = feature.geometry as Geometry;
        switch (geometry.type) {
          case "Point": {
            bounds.extend(geometry.coordinates as [number, number]);
            break;
          }
          case "LineString": {
            (geometry.coordinates as [number, number][]).forEach((coords) =>
              bounds.extend(coords),
            );
            break;
          }
          default:
            break;
        }
      }
    });
  }
}

/**
 * LOGIC
 */

function buildSimplifiedGeoJson(geoJson: FeatureCollection): FeatureCollection {
  const linesOnlyGeoJson: FeatureCollection = {
    type: "FeatureCollection",
    features: [],
  };

  if (geoJson.features.length) {
    const firstItemProps = geoJson.features[0].properties as GeoJsonNodeProps;

    var previousOsaWayId = firstItemProps.osaWayId;
    var previousOsaWaySelected = firstItemProps.osaWaySelected;
    var previousTransparent = firstItemProps.transparent;
    var previousColor = firstItemProps.color;
    var currentSegment: Position[] = [];

    geoJson.features.forEach((feature, index, list) => {
      const isLastFeature = index === list.length - 1;

      const { geometry } = feature;

      if (geometry.type === "Point") {
        const props = feature.properties as GeoJsonNodeProps;

        if (previousOsaWayId === props.osaWayId) {
          currentSegment.push(geometry.coordinates);
        }

        if (
          previousColor !== props.color || // new color starts
          previousOsaWayId !== props.osaWayId || // new way starts
          props.nodeSelected || // makes sure the point is centered with the line
          isLastFeature // makes sure the last segment is included
        ) {
          if (currentSegment.length > 1) {
            linesOnlyGeoJson.features.push({
              type: "Feature",
              properties: {
                color: previousColor,
                osaWaySelected: previousOsaWaySelected,
                osaWayId: previousOsaWayId,
                transparent: previousTransparent,
              } as GeoJsonNodeProps,
              geometry: {
                type: "LineString",
                coordinates: currentSegment,
              },
            });
          }

          currentSegment = [geometry.coordinates];
        }

        if (props.nodeSelected) {
          linesOnlyGeoJson.features.push(feature);
        }

        previousColor = props.color;
        previousOsaWaySelected = props.osaWaySelected;
        previousOsaWayId = props.osaWayId;
        previousTransparent = props.transparent;
      }
    });
  }

  return turf.simplify(linesOnlyGeoJson, {
    tolerance: simplificationTolerance,
    highQuality: false,
    mutate: true,
  });
}

interface ExtendBoundsOptions {
  selectedOsaNode?: OsaNode;
  selectedOsaWay?: OsaWay;
  selectedHlaWay?: HlaWay;
}
const shouldIncludeInBounds = (
  props: GeoJsonNodeProps,
  { selectedOsaNode, selectedOsaWay, selectedHlaWay }: ExtendBoundsOptions,
) => {
  if (selectedHlaWay) return false;
  if (selectedOsaNode) return props.id === selectedOsaNode.id;
  if (selectedOsaWay) return props.osaWayId === selectedOsaWay.id;
  return true;
};

const zoomThreshhold = 16;
const simplificationTolerance = 0.00004;
const weakOpacity = 0.5;

const isLineString = ["==", "$type", "LineString"];
const isPoint = ["==", "$type", "Point"];
const isNodeSelected = ["boolean", ["get", "nodeSelected"], false];
const isMultiNodeSelected = ["boolean", ["get", "nodeMultiSelected"], false];
const isPhotoMissing = ["boolean", ["get", "photoMissing"], false];
const isOsaWaySelected = ["boolean", ["get", "osaWaySelected"], false];
const isTransparent = ["boolean", ["get", "transparent"], false];

/**
 * TEMPLATES
 */

const lineLayerConfigTemplate: mapboxgl.LineLayerSpecification = {
  id: "",
  type: "line",
  source: "",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": ["get", "color"],
    "line-width": 7,
  },
  filter: isLineString,
};

const lineStrokeLayerConfigTemplate: mapboxgl.LineLayerSpecification = {
  id: "",
  source: "",
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-opacity": ["case", isTransparent, weakOpacity, 1],
    "line-color": defaultStrokeColor,
    "line-width": ["case", isOsaWaySelected, 11, 9],
  },
  filter: isLineString,
};

const directionLayerConfigTemplate: mapboxgl.SymbolLayerSpecification = {
  id: "",
  source: "",
  type: "symbol",
  layout: {
    "symbol-placement": "line",
    "symbol-spacing": 50,
    "icon-image": "chevron-icon",
    "icon-rotation-alignment": "map",
    "icon-size": 0.5,
  },
  paint: {
    "icon-color": defaultStrokeColor,
  },
};

/**
 * MAIN SOURCE
 */

const sourceName = CustomSource.Osa;
const sourceConfig: mapboxgl.GeoJSONSourceSpecification = {
  type: "geojson",
  promoteId: "id",
  data: {
    type: "FeatureCollection",
    features: [],
  },
};

// The node lines
const lineLayerName = `${sourceName}-line`;
const lineLayerConfig: mapboxgl.LineLayerSpecification = {
  ...lineLayerConfigTemplate,
  id: lineLayerName,
  source: sourceName,
  minzoom: zoomThreshhold,
};

// The stroke around the node lines
const strokeLayerName = `${sourceName}-stroke`;
const strokeLayerConfig: mapboxgl.LineLayerSpecification = {
  ...lineStrokeLayerConfigTemplate,
  source: sourceName,
  id: strokeLayerName,
  minzoom: zoomThreshhold,
};

// The center of the node dots
const pointLayerName = `${sourceName}-point`;
const pointLayerConfig: mapboxgl.CircleLayerSpecification = {
  id: pointLayerName,
  type: "circle",
  source: sourceName,
  paint: {
    "circle-radius": ["case", isNodeSelected, 9, 4],
    "circle-color": ["case", isPhotoMissing, "#ff0000", ["get", "color"]],
  },
  minzoom: zoomThreshhold,
  filter: isPoint,
};

// The stroke around the node dots
const pointStrokeLayerName = `${sourceName}-point-stroke`;
const pointStrokeLayerConfig: mapboxgl.CircleLayerSpecification = {
  id: pointStrokeLayerName,
  type: "circle",
  source: sourceName,
  paint: {
    "circle-opacity": ["case", isTransparent, weakOpacity, 1],
    "circle-radius": ["case", isNodeSelected, 11, 8],
    "circle-color": defaultStrokeColor,
  },
  minzoom: zoomThreshhold,
  filter: isPoint,
};

// Invisible dots that enable us to select nodes from the map even if we don't want to show them to the user
const pointInvisibleLayerName = `${sourceName}-invisible-point`;
const pointInvisibleLayerConfig: mapboxgl.CircleLayerSpecification = {
  id: pointInvisibleLayerName,
  type: "circle",
  source: sourceName,
  paint: {
    "circle-radius": 12,
    "circle-color": "transparent",
  },
  filter: isPoint,
};

// The blue dots that appear on high zoom when you select multiple nodes
const pointMultiSelectedLayerName = `${sourceName}-multi-selected-point`;
const pointMultiSelectedLayerConfig: mapboxgl.CircleLayerSpecification = {
  id: pointMultiSelectedLayerName,
  type: "circle",
  source: sourceName,
  paint: {
    "circle-radius": [
      "case",
      isMultiNodeSelected,
      ["case", isNodeSelected, 15, 11],
      0,
    ],
    "circle-color": "#00b7db",
  },
  minzoom: zoomThreshhold,
  filter: isPoint,
};

// The blue line that appears on low zoom when you select multiple nodes
const lineMultiSelectedLayerName = `${sourceName}-multi-selected-line`;
const lineMultiSelectedLayerConfig: mapboxgl.LineLayerSpecification = {
  ...lineStrokeLayerConfigTemplate,
  id: lineMultiSelectedLayerName,
  source: sourceName,
  paint: {
    "line-width": ["case", isMultiNodeSelected, 3, 0],
    "line-color": "#00b7db",
  },
  maxzoom: zoomThreshhold,
};

// The comment icon that appears on nodes with comments
const commentLayerName = `${sourceName}-comment`;
const commentLayerConfig: mapboxgl.SymbolLayerSpecification = {
  id: commentLayerName,
  type: "symbol",
  source: sourceName,
  layout: {
    "icon-image": "comment-icon",
    "icon-size": 1.0,
    "icon-offset": [24, 0],
  },
  filter: ["all", isPoint, ["==", "showCommentIcon", true]],
};

// Adds arrows to the way lines so that you know the direction
const directionLayerName = `${sourceName}-direction`;
const directionLayerConfig: mapboxgl.SymbolLayerSpecification = {
  ...directionLayerConfigTemplate,
  id: directionLayerName,
  source: sourceName,
  minzoom: zoomThreshhold,
};

/**
 * SIMPLIFIED SOURCE
 */

const simplifiedSourceName = CustomSource.SimplifiedOsa;
const simplifiedSourceConfig: mapboxgl.GeoJSONSourceSpecification = {
  type: "geojson",
  promoteId: "id",
  data: {
    type: "FeatureCollection",
    features: [],
  },
};

const simplifiedStrokeLayerName = `${simplifiedSourceName}-stroke`;
const simplifiedStrokeLayerConfig: mapboxgl.LineLayerSpecification = {
  ...lineStrokeLayerConfigTemplate,
  source: simplifiedSourceName,
  id: simplifiedStrokeLayerName,
  maxzoom: zoomThreshhold,
  minzoom: 12,
};

const simplifiedLineLayerName = `${simplifiedSourceName}-line`;
const simplifiedLineLayerConfig: mapboxgl.LineLayerSpecification = {
  ...lineLayerConfigTemplate,
  id: simplifiedLineLayerName,
  source: simplifiedSourceName,
  maxzoom: zoomThreshhold,
};

const simplifiedPointLayerName = `${simplifiedSourceName}-point`;
const simplifiedPointLayerConfig: mapboxgl.CircleLayerSpecification = {
  id: simplifiedPointLayerName,
  type: "circle",
  source: simplifiedSourceName,
  paint: {
    "circle-radius": 11,
    "circle-color": defaultStrokeColor,
  },
  maxzoom: zoomThreshhold,
  filter: isPoint,
};

const simplifiedDirectionLayerName = `${simplifiedSourceName}-direction`;
const simplifiedDirectionLayerConfig: mapboxgl.SymbolLayerSpecification = {
  ...directionLayerConfigTemplate,
  id: simplifiedDirectionLayerName,
  source: simplifiedSourceName,
  maxzoom: zoomThreshhold,
};

export const osaNodeShiftDragLayerName = pointInvisibleLayerName;
